import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Bot, User, Star, Loader2 } from 'lucide-react';
import { useRGPDGamification } from '../hooks/useRGPDGamification';
import { api } from '../context/AuthContext';

// Lightweight local Q&A chatbot — no LLM cost. Matches Fodindin editorial style.
// Adding a new answer? Just push to KB below.

const KB = [
  { keywords: ['bonjour', 'salut', 'hello', 'hey'], answer: "Bonjour ! Je suis l'assistant sécurité de Fodindin. Que souhaitez-vous savoir sur la protection de vos données ou de vos cagnottes ?" },
  { keywords: ['sécurité', 'protection', 'crypt', 'chiffr'], answer: "Vos données sensibles sont chiffrées en AES‑256. Les mots de passe utilisent bcrypt. Toutes les communications passent en TLS 1.3. Les clés API (Paystack, CinetPay) sont stockées côté serveur uniquement." },
  { keywords: ['mobile money', 'wave', 'orange', 'mtn', 'moov', 'momo'], answer: "Nous prenons en charge Wave, Orange Money, MTN MoMo et Moov Money via CinetPay. Chaque transaction requiert une confirmation opérateur (code USSD ou push) — Fodindin ne stocke jamais votre code secret." },
  { keywords: ['paystack', 'carte', 'bancaire', 'visa'], answer: "Les paiements par carte sont traités par Paystack (certifié PCI‑DSS niveau 1). Fodindin ne voit jamais votre numéro de carte : Paystack renvoie uniquement une référence de transaction signée." },
  { keywords: ['otp', 'code', 'sms'], answer: "Un code OTP à 6 chiffres est envoyé à votre téléphone pour chaque connexion. Il expire après 10 minutes et 5 tentatives infructueuses le bloquent — le seul moyen d'accéder à votre compte est de recevoir un SMS sur votre numéro." },
  { keywords: ['biométr', 'empreinte', 'face'], answer: "La biométrie est optionnelle et gérée par votre appareil (WebAuthn). Fodindin ne stocke jamais l'empreinte elle-même — uniquement une clé cryptographique publique associée à votre compte." },
  { keywords: ['supprim', 'effac', 'oubli', 'delete'], answer: "Vous pouvez demander la suppression de votre compte à tout moment via dpo@fodindincagnotte.com. Vos données personnelles sont anonymisées sous 30 jours. Les cagnottes clôturées restent archivées 5 ans pour conformité fiscale (Loi 2016-13 CI)." },
  { keywords: ['export', 'télécharg', 'donn', 'portabilité'], answer: "Vous avez le droit d'exporter toutes vos données (dons, cagnottes créées, profil) au format JSON. Écrivez à dpo@fodindincagnotte.com et nous vous répondons sous 48 h ouvrées." },
  { keywords: ['partag', 'vend', 'tiers'], answer: "Aucune donnée n'est vendue. Nous partageons strictement le minimum avec Paystack/CinetPay (montant, référence) pour traiter votre paiement. Aucun tracker publicitaire (Facebook Pixel, Google Ads) n'est présent sur le site." },
  { keywords: ['cookie'], answer: "Nous utilisons uniquement des cookies techniques (session, préférences RGPD). Zéro cookie publicitaire, zéro cookie tiers. Vous pouvez rejeter le bandeau — le site fonctionne parfaitement sans." },
  { keywords: ['fraud', 'suspect', 'piratage', 'hack'], answer: "Contactez immédiatement dpo@fodindincagnotte.com avec l'objet URGENT. Nous gelons votre compte en moins de 2 h et déclenchons une enquête. Vous serez également recontacté par SMS pour confirmer votre identité." },
  { keywords: ['loi', 'rgpd', 'conformité', 'legal'], answer: "Fodindin est conforme au RGPD (UE 2016/679), à la Loi ivoirienne 2016‑13 sur la protection des données personnelles et aux directives UEMOA. Notre DPO est joignable à dpo@fodindincagnotte.com." },
  { keywords: ['dpo', 'délégué', 'contact'], answer: "Notre Délégué à la Protection des Données (DPO) : dpo@fodindincagnotte.com. Réponse garantie sous 48 h ouvrées, en français." },
  { keywords: ['certificat', 'attestation', 'pdf'], answer: "Vous pouvez télécharger votre attestation RGPD nominative en PDF depuis la section « Attestation » de la charte. Elle est signée numériquement avec la date et un identifiant unique." },
  { keywords: ['droit', 'accès', 'accéder'], answer: "Vos droits RGPD : accès, rectification, effacement, portabilité, opposition, limitation. Toutes les demandes passent par dpo@fodindincagnotte.com et sont traitées sous 30 jours max (article 12 RGPD)." },
  { keywords: ['langue', 'wolof', 'dioula', 'bambara', 'peul'], answer: "La charte est en français pour l'instant. Les traductions en wolof, dioula, bambara et peul sont prévues en Q3 2026 — inscrivez‑vous à la newsletter pour être prévenu." },
];

function findAnswer(input) {
  const q = input.toLowerCase().trim();
  if (!q) return null;
  for (const entry of KB) {
    if (entry.keywords.some((k) => q.includes(k))) return entry.answer;
  }
  return "Désolé, je n'ai pas trouvé de réponse précise. Écrivez à dpo@fodindincagnotte.com — un humain vous répondra en 48 h. Vous pouvez aussi consulter les sections « Vos droits » et « Sécurité » ci‑dessus.";
}

const FEEDBACK_KEYWORDS = ['avis', 'feedback', 'noter', 'note', 'satisfait', 'expérience', 'experience'];

function isFeedbackIntent(input) {
  const q = input.toLowerCase().trim();
  return FEEDBACK_KEYWORDS.some((k) => q.includes(k));
}

function FeedbackForm({ onSubmitted, onError }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!rating || sending) return;
    setSending(true);
    try {
      await api.post('/feedback', {
        rating,
        comment: comment.trim() || null,
        type: 'general',
        page: window.location.pathname,
        cagnotteId: window.location.pathname.startsWith('/cagnotte/') ? window.location.pathname.split('/').pop() : null,
      });
      onSubmitted(rating);
    } catch (e) {
      onError();
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-black/5 p-3.5 space-y-2.5" data-testid="chatbot-feedback-form">
      <div className="text-[12.5px] font-semibold text-[#1a1d1a]">Notez votre expérience</div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`feedback-star-${n}`}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <Star className={`w-6 h-6 ${(hover || rating) >= n ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-neutral-300'}`} />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, 500))}
        placeholder="Votre commentaire (optionnel)…"
        data-testid="feedback-comment-input"
        rows={3}
        className="w-full rounded-xl bg-[#f5f4ef] px-3 py-2 text-[12.5px] outline-none focus:ring-2 focus:ring-[#d97757]/40 resize-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={!rating || sending}
        data-testid="feedback-submit-btn"
        className="w-full h-9 rounded-full bg-[#d97757] hover:bg-[#c66745] disabled:opacity-40 text-white text-[12.5px] font-medium flex items-center justify-center gap-1.5 transition-colors"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer mon avis
      </button>
    </div>
  );
}

let msgSeq = 0;
const mkMsg = (role, text) => ({ id: `m-${++msgSeq}`, role, text });

export default function RGPDChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(() => [
    mkMsg('bot', "Bonjour ! Posez‑moi vos questions sur la sécurité, vos droits ou la conformité RGPD de Fodindin. Vous pouvez aussi donner votre avis sur la plateforme."),
  ]);
  const [showFeedback, setShowFeedback] = useState(false);
  const listRef = useRef(null);
  const { unlock } = useRGPDGamification();

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, showFeedback]);

  const startFeedback = () => {
    setMessages((prev) => [...prev,
      mkMsg('user', 'Donner mon avis'),
      mkMsg('bot', 'Avec plaisir ! Notez votre expérience ci‑dessous et laissez un commentaire si vous le souhaitez.'),
    ]);
    setShowFeedback(true);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    if (isFeedbackIntent(text)) {
      setMessages((prev) => [...prev,
        mkMsg('user', text),
        mkMsg('bot', 'Avec plaisir ! Notez votre expérience ci‑dessous et laissez un commentaire si vous le souhaitez.'),
      ]);
      setShowFeedback(true);
      setInput('');
      return;
    }
    setMessages((prev) => [...prev, mkMsg('user', text), mkMsg('bot', findAnswer(text))]);
    setInput('');
    unlock('chatbot-used');
  };

  const suggestions = ['Sécurité', 'Mobile Money', 'Mes droits', 'Donner mon avis'];

  return (
    <>
      {/* Floating toggle button (bottom-left to avoid the Emergent badge on bottom-right) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fermer le chatbot RGPD' : 'Ouvrir le chatbot RGPD'}
        data-testid="rgpd-chatbot-toggle"
        className="fixed z-[9998] bottom-4 left-4 w-14 h-14 rounded-full bg-[#1a1d1a] text-white shadow-2xl hover:bg-black transition-all hover:scale-105 flex items-center justify-center"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div
          data-testid="rgpd-chatbot-panel"
          className="fixed z-[9998] bottom-20 left-4 right-4 sm:right-auto sm:w-[380px] rounded-2xl bg-white border border-black/10 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-300"
        >
          <div className="px-4 py-3 bg-[#1a1d1a] text-white flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#5a8268] flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[13.5px] font-semibold">Assistant Sécurité</div>
              <div className="text-[11px] text-white/60">Réponses instantanées, 100 % locales</div>
            </div>
          </div>
          <div ref={listRef} className="max-h-[340px] overflow-y-auto p-4 space-y-3 bg-[#f5f4ef]">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'bot' && (
                  <div className="w-7 h-7 rounded-full bg-[#5a8268] text-white flex items-center justify-center shrink-0"><Bot className="w-3.5 h-3.5" /></div>
                )}
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-[12.5px] leading-relaxed ${m.role === 'user' ? 'bg-[#d97757] text-white rounded-tr-sm' : 'bg-white text-[#1a1d1a] border border-black/5 rounded-tl-sm'}`}>
                  {m.text}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-[#1a1d1a] text-white flex items-center justify-center shrink-0"><User className="w-3.5 h-3.5" /></div>
                )}
              </div>
            ))}
            {showFeedback && (
              <FeedbackForm
                onSubmitted={(rating) => {
                  setShowFeedback(false);
                  setMessages((prev) => [...prev, mkMsg('bot', `Merci pour votre note de ${rating}/5 ! Votre avis nous aide à améliorer Fodindin.`)]);
                  unlock('chatbot-used');
                }}
                onError={() => {
                  setShowFeedback(false);
                  setMessages((prev) => [...prev, mkMsg('bot', "Désolé, une erreur est survenue lors de l'envoi. Réessayez dans un instant.")]);
                }}
              />
            )}
            {messages.length === 1 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`chatbot-suggestion-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => { if (s === 'Donner mon avis') { startFeedback(); } else { setInput(s); setTimeout(send, 50); } }}
                    className="text-[11.5px] px-2.5 py-1 rounded-full bg-white border border-black/10 hover:border-[#d97757] hover:text-[#d97757] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-2.5 border-t border-black/5 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Posez votre question…"
              data-testid="rgpd-chatbot-input"
              className="flex-1 h-9 rounded-full bg-[#f5f4ef] px-3.5 text-[12.5px] outline-none focus:ring-2 focus:ring-[#d97757]/40"
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim()}
              data-testid="rgpd-chatbot-send"
              className="w-9 h-9 rounded-full bg-[#1a1d1a] text-white hover:bg-black disabled:opacity-40 disabled:hover:bg-[#1a1d1a] flex items-center justify-center transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
